import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Send, MessageCircle } from 'lucide-react';
import { apiGetMensajes, apiEnviarMensaje } from '../api';

/**
 * ChatModal — Chat en tiempo real entre participantes de un viaje.
 * Props:
 *   viajeId       : ID del viaje activo
 *   usuarioId     : ID del usuario actual
 *   usuarioNombre : Nombre del usuario actual
 *   usuarioRol    : 'cliente' | 'transportista' | 'chofer'
 *   contraparte   : { id, nombre, rol } — con quién se chatea
 *   onClose       : función para cerrar
 */
export default function ChatModal({ viajeId, usuarioId, usuarioNombre, usuarioRol, contraparte, onClose, onCerrarConversacion }) {
  const [mensajes, setMensajes] = useState([]);
  const [texto, setTexto] = useState('');
  const [enviando, setEnviando] = useState(false);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);
  // Evita duplicados si el socket y el poll coinciden con el mismo mensaje
  const mensajesIdsRef = useRef(new Set());

  const cargarMensajes = useCallback(async () => {
    try {
      const data = await apiGetMensajes(viajeId);
      const lista = data || [];
      // Actualizar set de IDs conocidos
      lista.forEach(m => mensajesIdsRef.current.add(m.id));
      setMensajes(lista);
    } catch { /* silencioso */ }
  }, [viajeId]);

  // Carga inicial
  useEffect(() => {
    cargarMensajes();
  }, [cargarMensajes]);

  // Unirse/salir del room de viaje para recibir mensajes en tiempo real
  useEffect(() => {
    const socket = window.__choyeroSocket;
    if (socket) socket.emit('join_viaje', viajeId);

    // Re-unirse al room si el socket se reconecta mientras el modal está abierto
    const handleReconnect = () => {
      const s = window.__choyeroSocket;
      if (s) { s.emit('join_viaje', viajeId); cargarMensajes(); }
    };
    window.addEventListener('choyero_socket_connected', handleReconnect);

    return () => {
      const s = window.__choyeroSocket;
      if (s) s.emit('leave_viaje', viajeId);
      window.removeEventListener('choyero_socket_connected', handleReconnect);
    };
  }, [viajeId, cargarMensajes]);

  // Tiempo real: agregar mensaje directo del socket SIN HTTP call
  useEffect(() => {
    const handle = (e) => {
      const msg = e.detail?.mensaje;
      if (!msg || msg.viajeId !== viajeId) return;
      // Ignorar si ya lo tenemos (evita duplicado con el poll)
      if (mensajesIdsRef.current.has(msg.id)) return;
      mensajesIdsRef.current.add(msg.id);
      setMensajes(prev => [...prev, msg]);
    };
    window.addEventListener('choyero_nuevo_mensaje', handle);
    return () => window.removeEventListener('choyero_nuevo_mensaje', handle);
  }, [viajeId]);

  // Polling de respaldo cada 15s (ya no es el mecanismo principal)
  useEffect(() => {
    const iv = setInterval(cargarMensajes, 15000);
    return () => clearInterval(iv);
  }, [cargarMensajes]);

  // Scroll al último mensaje
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [mensajes.length]);

  // Focus al abrir
  useEffect(() => { inputRef.current?.focus(); }, []);

  // Cerrar con ESC
  useEffect(() => {
    const handle = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handle);
    return () => document.removeEventListener('keydown', handle);
  }, [onClose]);

  const handleEnviar = async () => {
    const trimmed = texto.trim();
    if (!trimmed || enviando) return;
    setTexto('');

    // Optimista: el mensaje aparece de inmediato sin esperar el API
    const tempId = `temp_${Date.now()}`;
    const mensajeOptimista = {
      id: tempId,
      viajeId,
      texto: trimmed,
      emisorId: usuarioId,
      emisorNombre: usuarioNombre,
      emisorRol: usuarioRol,
      destinatarioRol: contraparte.rol,
      creadoEn: new Date().toISOString(),
      emisor: { nombre: usuarioNombre, rol: usuarioRol }
    };
    mensajesIdsRef.current.add(tempId);
    setMensajes(prev => [...prev, mensajeOptimista]);

    setEnviando(true);
    try {
      const resp = await apiEnviarMensaje({ viajeId, texto: trimmed, destinatarioRol: contraparte.rol });
      // resp es el objeto mensaje directamente (apiEnviarMensaje ya extrae .mensaje)
      if (resp?.id) {
        mensajesIdsRef.current.add(resp.id);
        setMensajes(prev => prev.map(m => m.id === tempId ? resp : m));
      }
    } catch (e) {
      console.error('[Chat] Error enviando mensaje:', e.message);
      // Revertir: quitar el optimista y restaurar el texto
      setMensajes(prev => prev.filter(m => m.id !== tempId));
      mensajesIdsRef.current.delete(tempId);
      setTexto(trimmed);
    } finally {
      setEnviando(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleEnviar(); }
  };

  const formatHora = (iso) => new Date(iso).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
  const formatFecha = (iso) => {
    const d = new Date(iso);
    const hoy = new Date();
    if (d.toDateString() === hoy.toDateString()) return 'Hoy';
    const ayer = new Date(); ayer.setDate(ayer.getDate() - 1);
    if (d.toDateString() === ayer.toDateString()) return 'Ayer';
    return d.toLocaleDateString('es-MX', { day: 'numeric', month: 'short' });
  };

  // Filtrar mensajes del canal correcto
  const roles = new Set([usuarioRol, contraparte.rol]);
  const mensajesFiltrados = mensajes.filter(m => {
    if (!m || typeof m !== 'object' || !m.viajeId) return false;
    if (m.viajeId !== viajeId) return false;
    if (usuarioRol === 'admin') return true;
    if (m.emisorRol === 'admin') return m.destinatarioRol === usuarioRol;
    if (m.destinatarioRol) return roles.has(m.emisorRol) && roles.has(m.destinatarioRol);
    return roles.has(m.emisorRol);
  });

  // Agrupar por fecha
  const grupos = [];
  let fechaActual = null;
  mensajesFiltrados.forEach(m => {
    const fecha = formatFecha(m.creadoEn || m.fecha);
    if (fecha !== fechaActual) { grupos.push({ tipo: 'fecha', label: fecha }); fechaActual = fecha; }
    grupos.push({ tipo: 'mensaje', ...m });
  });

  const rolLabel = { cliente: 'Cliente', transportista: 'Transportista', chofer: 'Chofer', admin: 'Soporte Choyero' };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[1100]">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md flex flex-col" style={{ height: '520px' }}>

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b bg-gray-50 rounded-t-2xl">
          <div className="flex items-center space-x-3">
            <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center">
              <MessageCircle size={18} className="text-blue-600" />
            </div>
            <div>
              <p className="font-bold text-gray-800 text-sm">{contraparte.nombre}</p>
              <p className="text-xs text-gray-500">{rolLabel[contraparte.rol] || contraparte.rol}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {onCerrarConversacion && (
              <button onClick={onCerrarConversacion} className="text-xs px-2 py-1 bg-red-100 text-red-600 hover:bg-red-200 rounded-lg font-semibold">
                Cerrar conversación
              </button>
            )}
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1"><X size={20} /></button>
          </div>
        </div>

        {/* Mensajes */}
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-1">
          {grupos.length === 0 && (
            <div className="flex flex-col items-center justify-center h-full text-gray-400">
              <MessageCircle size={40} className="mb-3 opacity-30" />
              <p className="text-sm">Inicia la conversación</p>
              <p className="text-xs mt-1 opacity-70">Los mensajes son visibles solo entre ustedes</p>
            </div>
          )}
          {grupos.map((item, i) => {
            if (item.tipo === 'fecha') {
              return (
                <div key={`fecha-${i}-${item.label}`} className="flex justify-center my-3">
                  <span className="text-xs text-gray-400 bg-gray-100 px-3 py-1 rounded-full">{item.label}</span>
                </div>
              );
            }
            const esMio = item.emisorId === usuarioId;
            const esAdmin = item.emisorRol === 'admin';
            return (
              <div key={`msg-${item.id}-${i}`} className={`flex ${esMio ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[75%] ${esMio ? 'items-end' : 'items-start'} flex flex-col`}>
                  {!esMio && (
                    <p className={`text-xs mb-1 ml-1 ${esAdmin ? 'text-purple-600 font-semibold' : 'text-gray-500'}`}>
                      {esAdmin ? '🛡️ ' : ''}{item.emisorNombre}
                    </p>
                  )}
                  <div className={`px-4 py-2.5 rounded-2xl text-sm ${
                    esMio ? 'bg-blue-600 text-white rounded-br-sm'
                      : esAdmin ? 'bg-purple-100 text-purple-900 rounded-bl-sm border border-purple-200'
                      : 'bg-gray-100 text-gray-800 rounded-bl-sm'
                  }`}>
                    <p style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{item.texto}</p>
                  </div>
                  <p className="text-xs text-gray-400 mt-1 mx-1">{formatHora(item.creadoEn || item.fecha)}</p>
                </div>
              </div>
            );
          })}
          <div ref={bottomRef} />
        </div>

        {/* Input */}
        <div className="px-4 py-3 border-t flex items-end space-x-2">
          <textarea
            ref={inputRef}
            value={texto}
            onChange={e => setTexto(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Escribe un mensaje... (Enter para enviar)"
            rows={1}
            className="flex-1 resize-none px-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 max-h-24"
            style={{ overflowY: 'auto' }}
          />
          <button
            onClick={handleEnviar}
            disabled={!texto.trim() || enviando}
            className="w-10 h-10 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-200 text-white rounded-xl flex items-center justify-center transition flex-shrink-0"
          >
            <Send size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
